import './alteration-card.scss'

import _ from 'lodash'
import { PropType } from 'vue'
import Component from "vue-class-component"
import { Watch } from 'vue-property-decorator'
import { faPen, faEllipsisH, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons'

import { Alteration, ThesaurusTerm, createTranslatedString, editTranslatedString, getTranslatedString } from '@skinsoft/types'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { ThesaurusTermDataStore } from '@skinsoft/entity/src/thesaurusTerm'
import { ReactiveVue } from "@skinsoft/reactive-vue"
import { Media } from '@skinsoft/ui/src/item-card/media'
import ThesaurusTermTreeModal, { THESAURUS_TERM_SELECTED } from '@skinsoft/ui/src/thesaurus-term-tree/thesaurus-term-tree-modal'
import ThesaurusTermTree from '@skinsoft/ui/src/thesaurus-term-tree'
import { AutoCompleteTextField2, AutoCompleteTextField2Modal, FIELD_VALUE_UPDATED } from '@skinsoft/ui2'

export const ALTERATION_CARD_DELETE_ALTERATION = 'alteration-card-delete-alteration'
export const ALTERATION_CARD_ALTERATION_UPDATED = 'alteration-card-alteration-type-updated'

export enum MainAlterationCardField {
    Title,
    AlterationType,
}

export interface AlterationChanged {
    index: number,
    updates: Partial<Alteration>
}

export type AlterationWithThesaurusTerm = Alteration & { typeOfAlterationObject: ThesaurusTerm | null }

@Component({
    components: {
        sMedia: Media,
        sThesaurusTermTreeModal: ThesaurusTermTreeModal,
        sThesaurusTermTree: ThesaurusTermTree,
        sAutoCompleteTextField2: AutoCompleteTextField2,
        sAutoCompleteTextField2Modal: AutoCompleteTextField2Modal,
    },
    props: {
        type: Array,
        alteration: {
            type: Object as PropType<AlterationWithThesaurusTerm>,
        },
        index: {
            type: Number,
        },
        mainField: {
            default: MainAlterationCardField.Title
        },
        readOnly: {
            type: Boolean
        },
        existingTitles: {
            type: Array as PropType<String[]>,
            default:() => []
        },
    },
    template: require('./alteration-card.html')
})
export default class AlterationCard extends ReactiveVue {
    private index: number
    private alteration: AlterationWithThesaurusTerm
    private mainField: MainAlterationCardField
    private readOnly: Boolean
    private existingTitles: String[]

    // @ts-ignore
    private updatedAlteration: AlterationWithThesaurusTerm = {}
    private isEditing = false
    private moreMenuOpen = false
    private isDeleteConfirmationOpen = false

    private $thesaurusTermDataStore: ThesaurusTermDataStore

    private editIcon = faPen
    private moreIcon = faEllipsisH
    private cancelIcon = faTimes
    private validateIcon = faCheck

    private THESAURUS_TERM_SELECTED = THESAURUS_TERM_SELECTED
    private FIELD_VALUE_UPDATED = FIELD_VALUE_UPDATED

    private MAIN_FIELD_TITLE = MainAlterationCardField.Title
    private MAIN_FIELD_ALTERATION_TYPE = MainAlterationCardField.AlterationType

    $refs: {
        alterationTypeModal: ThesaurusTermTreeModal,
        alterationTitleModal: AutoCompleteTextField2Modal,
    }

    private mounted() {
        this.updatedAlteration = _.cloneDeep(this.alteration)
    }

    private onEditingCanceled() {
        this.updatedAlteration = _.cloneDeep(this.alteration)
        this.toggleEdit()
    }

    private openThesaurusTermModal() {
        this.$refs.alterationTypeModal.openModal()
        this.moreMenuOpen = false
    }

    private openTitleModal() {
        this.$refs.alterationTitleModal.openModal()
        this.moreMenuOpen = false
    }

    private onTypeOfAlterationChanged(thesaurusTermId: string) {
        this.updatedAlteration.typeOfAlteration = thesaurusTermId
        this.$thesaurusTermDataStore.fetchOne(thesaurusTermId)
            .subscribe(thesaurusTerm => {
                if(thesaurusTerm) {
                    this.updatedAlteration.typeOfAlterationObject = thesaurusTerm
                }else {
                    this.updatedAlteration.typeOfAlterationObject = null
                }
            })
    }

    private updateAlteration() {
        let updates = _.cloneDeep(this.updatedAlteration)
        // @ts-ignore
        delete updates.typeOfAlterationObject
        // @ts-ignore
        delete this.updatedAlteration.index

        this.$emit(ALTERATION_CARD_ALTERATION_UPDATED, { index: this.index, updates: updates } as AlterationChanged)
        this.toggleEdit()
    }

    private openDeleteConfirmation() {
        if(!this.readOnly) {
            this.isDeleteConfirmationOpen = true
        }
    }

    private deleteAlteration() {
        this.$emit(ALTERATION_CARD_DELETE_ALTERATION, this.index)
    }

    private toggleEdit() {
        if(!this.readOnly) {
            this.isEditing = !this.isEditing
            this.moreMenuOpen = false
        }
    }

    private toggleMoreMenu() {
        if(!this.readOnly) {
            this.moreMenuOpen = !this.moreMenuOpen
            this.isDeleteConfirmationOpen = false
        }
    }

    private get mainFieldValue(): string {
        switch(this.mainField) {
            case MainAlterationCardField.Title:
                return getTranslatedString(this.updatedAlteration.title, this.getAppLocale())
            case MainAlterationCardField.AlterationType:
                if(this.updatedAlteration.typeOfAlterationObject) {
                    return getTranslatedString(
                        thesaurusTermSelectors.preferredTerm(this.updatedAlteration.typeOfAlterationObject), 
                        this.getAppLocale()
                    )
                }
                break
        }

        return ''
    }

    private get updatedAlterationTitle(): string {
        return getTranslatedString(this.updatedAlteration.title, this.getAppLocale())
    }
    private set updatedAlterationTitle(value: string) {
        if(this.updatedAlteration.title) {
            editTranslatedString(this.updatedAlteration.title, value, this.getAppLocale())
        } else {
            _.set(this.updatedAlteration, 'title', createTranslatedString(this.getAppLocale(), value))
        }
    }
    private onTitleUpdated(value: String) {
        this.updatedAlterationTitle = value as string
    }

    private get updatedAlterationPrecision(): string {
        return getTranslatedString(this.updatedAlteration.precision, this.getAppLocale())
    }
    private set updatedAlterationPrecision(value: string) {
        if(this.updatedAlteration.precision) {
            editTranslatedString(this.updatedAlteration.precision, value, this.getAppLocale())
        } else {
            _.set(this.updatedAlteration, 'precision', createTranslatedString(this.getAppLocale(), value))
        }
    }

    @Watch('alteration')
    private onAlterationChanged() {
        this.updatedAlteration = _.cloneDeep(this.alteration)
        this.moreMenuOpen = false
        this.isEditing = false
    }
}